import { useState } from 'react';
import { Box, Divider, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { trackingService } from '../services/tracking.service';
import { costLagState, worstSource, ageMs, shortAge } from '../utils/data-freshness';
import { formatLocal, localZoneLabel } from '../utils/timezone';

const REFRESH_MS = 60_000;

function headline(worst: ReturnType<typeof worstSource>): string {
  if (!worst) return 'No source is running traffic';
  if (worst.behind_minutes === null) return `No cost reported for ${worst.name}`;
  return `Cost is ${shortAge(worst.behind_minutes * 60_000).replace(' ago', '')} behind on ${worst.name}`;
}

export default function DataFreshness() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { data } = useQuery({
    queryKey: ['tracking-freshness'],
    queryFn: () => trackingService.getFreshness(),
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const late = costLagState(data) === 'late';
  const worst = worstSource(data);

  return (
    <>
      <Tooltip title={late ? 'Cost data is behind' : 'How current this report is'}>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Box sx={{ position: 'relative', display: 'flex' }}>
            <Schedule fontSize="small" color={late ? 'warning' : 'inherit'} />
            {late && (
              <Box sx={{
                position: 'absolute', top: -1, right: -1, width: 6, height: 6,
                borderRadius: '50%', bgcolor: 'warning.main',
              }} />
            )}
          </Box>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2">Data freshness</Typography>
          <Typography
            variant="h6"
            color={late ? 'warning.main' : 'text.primary'}
            sx={{ mt: 0.5, lineHeight: 1.25 }}
          >
            {headline(worst)}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '6px 14px', mt: 1.75 }}>
            <Typography variant="body2" color="text.secondary">Traffic</Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {data?.traffic_through ? formatLocal(data.traffic_through) : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {shortAge(ageMs(data?.traffic_through ?? null))}
            </Typography>

            <Typography variant="body2" color="text.secondary">Conversions</Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {data?.conversions_through ? formatLocal(data.conversions_through) : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {shortAge(ageMs(data?.conversions_through ?? null))}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" color="text.secondary">
            Cost, per source carrying traffic today
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '6px 14px', mt: 1 }}>
            {(data?.sources ?? []).map((s) => {
              const missing = s.behind_minutes === null;
              return (
                <Box key={s.name} sx={{ display: 'contents' }}>
                  <Typography variant="body2" color="text.secondary">{s.name}</Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {s.cost_covers_through ? formatLocal(s.cost_covers_through) : '—'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={!missing && (s.behind_minutes ?? 0) > 120 ? 'warning.main' : 'text.secondary'}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {missing ? 'no cost' : shortAge(s.behind_minutes! * 60_000).replace(' ago', ' behind')}
                  </Typography>
                </Box>
              );
            })}
            {!(data?.sources ?? []).length && (
              <Typography variant="body2" color="text.secondary">No source is running traffic today.</Typography>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Traffic and conversions are live; cost arrives in hourly buckets. A source with
            no cost may be paused rather than broken. Times in {localZoneLabel()}.
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
